import {useContext, useMemo, useState} from 'preact/hooks';
import {useCallback} from 'react';

import {Scenario} from 'utilities/promise-presenter';
import {Line, Slot, UiLineType} from 'layouts';
import {ExpiryTimerHourMinute, TemplateOutput} from 'components';
import {AppState} from 'utilities/app-state';
import {useResizeObserver} from 'utilities/dom';
import {useI18n} from 'utilities/i18n';

import {
  FormattedAddress,
  ShopPromiseExpansionBlock,
  CappedList,
  Optional,
} from './components';

interface Props {
  promisePresentment: Scenario;
  isLoading: boolean;
  hasPostalCodeOverride?: boolean;
}

const TRUNCATE_WIDTH_THRESHOLD = 295;

export function ShopPromiseBrand({
  promisePresentment,
  isLoading,
  hasPostalCodeOverride,
}: Props) {
  const {mainButtonRef} = useContext(AppState);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const reviewAvailableSpace = useCallback(
    (rect: DOMRectReadOnly) => {
      setShouldTruncate(
        Boolean(
          (promisePresentment.destinationLineHasPrefix &&
            rect.width < TRUNCATE_WIDTH_THRESHOLD) ||
            hasPostalCodeOverride,
        ),
      );
    },
    [hasPostalCodeOverride, promisePresentment.destinationLineHasPrefix],
  );
  // todo: Revisit usage of `mainButtonRef`
  useResizeObserver(mainButtonRef, reviewAvailableSpace);
  const {translate} = useI18n();

  const logoImg = promisePresentment.logoSrc;

  const formattedAddressMarkup = useMemo(
    () => (
      <FormattedAddress
        truncate={shouldTruncate}
        address={promisePresentment.destinationAddress}
      />
    ),
    [promisePresentment, shouldTruncate],
  );

  const replacements = useMemo(
    () => new Map<string, unknown>([['destination', formattedAddressMarkup]]),
    [formattedAddressMarkup],
  );

  const destinationLineMarkup = useMemo(
    () => (
      <TemplateOutput
        template={promisePresentment.locationPrefix}
        replacements={replacements}
      />
    ),
    [replacements, promisePresentment.locationPrefix],
  );

  const destinationUiLineType = useMemo(() => {
    return promisePresentment.destinationLineHasPrefix
      ? UiLineType.Destination
      : UiLineType.DestinationFullWidth;
  }, [promisePresentment.destinationLineHasPrefix]);

  const contentMarkup = useMemo(
    () => (
      <>
        <Line type={UiLineType.TitleWithBrand}>{promisePresentment.title}</Line>
        <Line type={destinationUiLineType}>{destinationLineMarkup}</Line>
      </>
    ),
    [promisePresentment.title, destinationUiLineType, destinationLineMarkup],
  );

  const additionalInfo = useMemo(
    () => (
      <Line type={UiLineType.AdditionalInfo}>
        {!promisePresentment.title && (
          <CappedList limit={1}>
            <Optional>
              <Line type={UiLineType.TrackingIncluded}>
                {translate('title.extra.trackingIncluded')}
              </Line>
            </Optional>
            {promisePresentment.expiry && (
              <Optional>
                <Line type={UiLineType.ExpiryLine}>
                  <ExpiryTimerHourMinute dateDiff={promisePresentment.expiry} />
                </Line>
              </Optional>
            )}
            {promisePresentment.conditionallyFree && (
              <Optional>
                <Line type={UiLineType.ConditionallyFree}>
                  {promisePresentment.conditionallyFree}
                </Line>
              </Optional>
            )}
            {promisePresentment.fasterOptionsAvailableExtraLineMessage && (
              <Optional>
                <Line type={UiLineType.FasterOptions}>
                  {promisePresentment.fasterOptionsAvailableExtraLineMessage}
                </Line>
              </Optional>
            )}
          </CappedList>
        )}
      </Line>
    ),
    [
      promisePresentment.conditionallyFree,
      promisePresentment.expiry,
      promisePresentment.fasterOptionsAvailableExtraLineMessage,
      promisePresentment.title,
      translate,
    ],
  );

  const brandMarkup = useMemo(
    () => (
      <Line type={UiLineType.BrandImage}>
        {logoImg && (
          <img
            src={logoImg}
            alt={promisePresentment.logoAltText}
            data-brand-handle={promisePresentment.brandHandle}
          />
        )}
      </Line>
    ),
    [logoImg, promisePresentment.brandHandle, promisePresentment.logoAltText],
  );

  const genericMarkup = (
    <ShopPromiseExpansionBlock
      showLogo={promisePresentment.showLogo}
      isLoading={isLoading}
    />
  );

  return (
    <>
      <Slot name="Content">{contentMarkup}</Slot>
      <Slot name="Brand">{brandMarkup}</Slot>
      {additionalInfo}
      {genericMarkup}
    </>
  );
}
